import PortalShell from "../../components/PortalShell";
import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { CheckCircle2, XCircle, RotateCcw } from "lucide-react";

export default function Quiz() {
  const [topics, setTopics] = useState([]);
  const [cfg, setCfg] = useState({ topic: "", board: "Edexcel", tier: "Higher", year: "", count: 10 });
  const [stage, setStage] = useState("setup"); // setup | active | results
  const [qs, setQs] = useState([]);
  const [ans, setAns] = useState({});
  const [started, setStarted] = useState(0);
  const [result, setResult] = useState(null);

  useEffect(() => { api.get("/questions/topics").then(r => setTopics(r.data.topics || [])); }, []);

  const start = async () => {
    try {
      const payload = { count: Number(cfg.count) };
      ["topic","board","tier"].forEach(k => { if (cfg[k]) payload[k] = cfg[k]; });
      if (cfg.year) payload.year = Number(cfg.year);
      const { data } = await api.post("/quiz/start", payload);
      if (!data.questions.length) return toast.error("No questions for those filters");
      setQs(data.questions); setAns({}); setStage("active"); setStarted(Date.now());
    } catch { toast.error("Couldn't start quiz"); }
  };

  const submit = async () => {
    const duration = Math.round((Date.now() - started) / 1000);
    try {
      const payload = { answers: ans, duration_seconds: duration };
      ["topic","board","tier"].forEach(k => { if (cfg[k]) payload[k] = cfg[k]; });
      if (cfg.year) payload.year = Number(cfg.year);
      const { data } = await api.post("/quiz/submit", payload);
      setResult(data); setStage("results");
    } catch { toast.error("Couldn't submit quiz"); }
  };

  const reset = () => { setStage("setup"); setResult(null); setQs([]); setAns({}); };

  return (
    <PortalShell role="student" title="Practice Quiz" subtitle="Instant grading. Full worked solutions. Every attempt logged.">
      {stage === "setup" && (
        <div className="bg-white rounded-xl border border-slate-200 p-8 max-w-2xl">
          <h3 className="font-serif text-xl brand-navy mb-6">Set up your quiz</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <Sel label="Board" val={cfg.board} set={v=>setCfg({...cfg, board:v})} options={["","AQA","Edexcel","OCR"]} testid="quiz-board"/>
            <Sel label="Tier" val={cfg.tier} set={v=>setCfg({...cfg, tier:v})} options={["","Foundation","Higher"]} testid="quiz-tier"/>
            <Sel label="Year (optional)" val={cfg.year} set={v=>setCfg({...cfg, year:v})} options={["",7,8,9,10,11]} testid="quiz-year"/>
            <Sel label="Topic (optional)" val={cfg.topic} set={v=>setCfg({...cfg, topic:v})} options={["", ...topics]} testid="quiz-topic"/>
            <Sel label="Questions" val={cfg.count} set={v=>setCfg({...cfg, count:v})} options={[5,10,15,20]} testid="quiz-count"/>
          </div>
          <button onClick={start} className="btn-gold mt-8 px-6 py-3 rounded-md" data-testid="quiz-start">Start quiz →</button>
        </div>
      )}

      {stage === "active" && (
        <div className="space-y-4">
          {qs.map((q, i) => (
            <div key={q.id} className="bg-white rounded-xl border border-slate-200 p-6">
              <div className="text-xs uppercase tracking-widest gold-text mb-2">Question {i+1} of {qs.length}</div>
              <div className="font-serif text-xl brand-navy mb-4">{q.question}</div>
              <div className="space-y-2">
                {q.options.map((o, idx) => (
                  <label key={idx} className={`flex items-center gap-3 p-3 rounded-md border cursor-pointer transition ${ans[q.id]===o ? 'border-brand-navy bg-slate-50' : 'border-slate-200 hover:bg-slate-50'}`} data-testid={`quiz-q${i}-opt${idx}`}>
                    <input type="radio" checked={ans[q.id]===o} onChange={()=>setAns({...ans, [q.id]: o})} className="accent-brand-navy" style={{accentColor:"#0A1128"}}/>
                    <span className="text-sm"><span className="text-slate-400 mr-2">{String.fromCharCode(65+idx)}.</span>{o}</span>
                  </label>
                ))}
              </div>
            </div>
          ))}
          <div className="flex items-center justify-between sticky bottom-4 bg-white rounded-xl border border-slate-200 p-4 shadow-lg">
            <span className="text-sm text-slate-600">{Object.keys(ans).length} / {qs.length} answered</span>
            <button onClick={submit} disabled={Object.keys(ans).length < qs.length} className="btn-gold px-6 py-2.5 rounded-md disabled:opacity-60" data-testid="quiz-submit">Submit quiz</button>
          </div>
        </div>
      )}

      {stage === "results" && result && (
        <div className="space-y-4">
          <div className="bg-gradient-to-br from-brand-navy to-slate-800 rounded-xl p-8 text-white" data-testid="quiz-result-banner">
            <div className="text-xs uppercase tracking-[0.25em] brand-gold mb-2">Result</div>
            <div className="flex items-baseline gap-4 mb-2">
              <span className="font-serif text-6xl">{result.score}</span>
              <span className="font-serif text-2xl text-white/60">/ {result.total}</span>
              <span className="ml-auto text-sm brand-gold">{Math.round(result.score/result.total*100)}% accuracy</span>
            </div>
            <p className="text-white/70">Every wrong answer is now in your error book. Review below.</p>
          </div>

          {result.results.map((r, i) => (
            <div key={r.id} className="bg-white rounded-xl border border-slate-200 p-6">
              <div className="flex items-start gap-3 mb-3">
                {r.is_correct ? <CheckCircle2 className="text-green-700 shrink-0 mt-1" size={20}/> : <XCircle className="text-red-700 shrink-0 mt-1" size={20}/>}
                <div className="flex-1">
                  <div className="text-xs uppercase tracking-widest text-slate-400 mb-1">Question {i+1}</div>
                  <div className="font-serif text-lg brand-navy">{r.question}</div>
                </div>
              </div>
              <div className="pl-8 text-sm space-y-1.5">
                <div>Your answer: <strong className={r.is_correct?'text-green-700':'text-red-700'}>{r.given || '(blank)'}</strong></div>
                {!r.is_correct && <div>Correct: <strong className="brand-navy">{r.correct}</strong></div>}
                <div className="text-slate-600 mt-2"><strong className="brand-navy">Solution:</strong> {r.explanation}</div>
              </div>
            </div>
          ))}

          <button onClick={reset} className="btn-ghost-navy px-6 py-3 rounded-md inline-flex items-center gap-2" data-testid="quiz-reset"><RotateCcw size={16}/> Take another</button>
        </div>
      )}
    </PortalShell>
  );
}

function Sel({label,val,set,options,testid}){return(<div><label className="text-[10px] uppercase tracking-widest text-slate-500 mb-1.5 block">{label}</label><select data-testid={testid} value={val} onChange={e=>set(e.target.value)} className="w-full border border-slate-300 rounded-md px-3 py-2.5 bg-white text-sm">{options.map((o,i)=><option key={i} value={o}>{o===""?"Any":o}</option>)}</select></div>);}
